import React, { useEffect, useState, useRef, Fragment } from "react";
import moment from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Typography } from "@mui/material";
import SwipeableViews from "react-swipeable-views-react-18-fix";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { CircularProgress } from "@mui/material";
import Zoom from "@mui/material/Zoom";
import { acquireToken } from "../../authentication/utils/authUtils";

import useLocale from "../../utils/useLocale";
import { TabPanel } from "./components/TabPanel";
import StudentCard from "./components/StudentCard";
import ParentCard from "./components/ParentCard";
import { formatTodaysDate } from "../../utils/dateUtils";
import { NotesList } from "./components/NotesList";
import { StudentSelector } from "./components/StudentSelector";

export const Reports = () => {
  useLocale();
  const theme = useTheme();
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  var today = new Date();
  const [startDate, setStartDate] = useState(
    new Date(today.getFullYear(), today.getMonth() - 1, 1)
  );
  const [endDate, setEndDate] = useState(new Date());
  const [selectedStudent, setSelectedStudent] = useState({});
  const [newNotes, setNewNotes] = useState("");
  const [students, setStudents] = useState([{}]);
  const [parentInfo, setParentInfo] = useState([{}]);
  const [studentAutocompleteValue, setStudentAutocompleteValue] = useState("");
  const [studentSelected, setStudentSelected] = useState(false);
  const [panelIndex, setPanelIndex] = React.useState(0);
  const [notes, setNotes] = useState([]);
  const [mobile, setMobile] = useState(false);
  const [attendance, setAttendance] = useState([{}]);
  const [loading, setLoading] = useState({
    students: true,
    notes: true,
    submitting: false,
  });

  const useDidMountEffect = (func, deps) => {
    const didMount = useRef(false);
    useEffect(() => {
      if (didMount.current) func();
      else didMount.current = true;
    }, deps);
  };

  useEffect(() => {
    const setResponsiveness = () => {
      return window.innerWidth < 900 ? setMobile(true) : setMobile(false);
    };

    setResponsiveness();
    window.addEventListener("resize", () => setResponsiveness());

    return () => {
      window.removeEventListener("resize", () => setResponsiveness());
    };
  }, []);

  useDidMountEffect(() => {
    async function getSelectedStudentNotes() {
      if (studentSelected) {
        let token = await acquireToken(instance, account);
        let data = {
          contactid: selectedStudent.contactid,
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          account: selectedStudent._fw_parentaccount_value,
        };
        const noteRequest = axios.get("/api/noteids", {
          params: data,
          headers: {
            Authorization: token.accessToken,
          },
        });
        let pictureRequest = null;

        pictureRequest = axios.get("/api/studentPicture", {
          params: data,
          headers: {
            Authorization: token.accessToken,
          },
        });

        let parentInfoRequest = axios.get("/api/account", {
          params: data,
          headers: {
            Authorization: token.accessToken,
          },
        });

        let attendanceRequest = axios.get("/api/getAttendance", {
          params: data,
          headers: {
            Authorization: token.accessToken,
          },
        });
        const [notesRes, pictureRes, parentInfoRes, attendanceRes] =
          await Promise.all([
            noteRequest,
            pictureRequest,
            parentInfoRequest,
            attendanceRequest,
          ]);
        if (
          pictureRes &&
          pictureRes.data &&
          pictureRes.data.res &&
          pictureRes.data.res.value
        ) {
          selectedStudent.entityimage =
            pictureRes.data.res.value[0].entityimage;
        }
        if (
          notesRes.data &&
          notesRes.data.res &&
          notesRes.data.res.value.length > 0
        ) {
          setNotes(notesRes.data.res.value);
        } else {
          setNotes([{ cr855_date: "", cr855_note: "" }]);
        }
        if (
          parentInfoRes.data &&
          parentInfoRes.data.res &&
          parentInfoRes.data.res.value
        ) {
          setParentInfo(parentInfoRes.data.res.value[0].primarycontactid);
        }
        if (
          attendanceRes.data &&
          attendanceRes.data.res &&
          attendanceRes.data.res.value
        ) {
          setAttendance(attendanceRes.data.res.value);
        }
      }
    }
    getSelectedStudentNotes();
  }, [selectedStudent, startDate, endDate]);

  useDidMountEffect(() => {
    setLoading((prev) => ({ ...prev, notes: false }));
  }, [notes]);

  useEffect(() => {
    async function fetchStudents() {
      let token = await acquireToken(instance, account);
      try {
        let studentsRes = await axios.get("/api/students", {
          headers: {
            Authorization: token.accessToken,
          },
        });
        setStudents(studentsRes.data.res.value);
        setLoading((prev) => ({ ...prev, students: false }));
      } catch (error) {
        console.error("Error fetching students", error);
      }
    }

    fetchStudents();
  }, []);

  const handleChangeTab = (event, newValue) => {
    setPanelIndex(newValue);
  };

  const handleSubmission = async () => {
    if (
      attendance.length == 0 ||
      attendance[0].cr855_date != formatTodaysDate()
    ) {
      alert("Please take attendance before submitting a note.");
      return;
    }
    if (loading.submitting) return;
    setLoading((prev) => ({ ...prev, submitting: true }));
    let today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to midnight
    let formattedNotes = newNotes.replace(/\n/g, "<br>");
    let token = await acquireToken(instance, account);
    let adjustedToday = today.toDateString().slice(0, 16); // Adjust the date format
    let contact = selectedStudent.contactid;
    let data = {
      cr855_date: adjustedToday,
      cr855_note: formattedNotes,
      cr855_coach: account.username,
      cr855_studentid: contact,
    };

    try {
      await axios.post("/api/createNote", data, {
        headers: { Authorization: token.accessToken },
      });

      let newNote = { date: adjustedToday, note: formattedNotes };

      setNotes((prevNotes) => [
        {
          cr855_date: moment(newNote.date).toISOString().slice(0, 10),
          cr855_note: newNote.note,
          cr855_coach: account.username,
        },
        ...prevNotes,
      ]);

      setNewNotes("");
    } catch (error) {
      console.error("Error submitting the note:", error);
    } finally {
      setLoading((prev) => ({ ...prev, submitting: false }));
      setPanelIndex(0);
    }
  };

  const handleStudentChange = (v) => {
    setLoading((prev) => ({ ...prev, notes: true }));
    setStudentAutocompleteValue(v);
    if (v != null) {
      setSelectedStudent(v);
      setStudentSelected(true);
    } else {
      setSelectedStudent({});
      setStudentSelected(false);
    }
  };

  const renderTabsContent = (width) => (
    <Zoom in={studentSelected}>
      <Box
        sx={{
          bgcolor: " #1A1A1A",

          minWidth: "50vw",
          padding: 1,
          boxShadow: 4,
          marginBottom: 5,
          minHeight: "100vh",
        }}
      >
        <AppBar style={{ background: " #2C2C2C" }} position="static">
          <Tabs
            value={panelIndex}
            onChange={handleChangeTab}
            TabIndicatorProps={{
              style: {
                backgroundColor: "#FFFFFF",
              },
            }}
            textColor="default"
            variant="fullWidth"
            aria-label="full width tabs example"
          >
            <Tab label="Report" />
            <Tab label="Create A Note" />
          </Tabs>
        </AppBar>
        <SwipeableViews
          axis={theme.direction === "rtl" ? "x-reverse" : "x"}
          index={panelIndex}
          onChangeIndex={setPanelIndex}
          style={{ width: "100%" }}
        >
          <TabPanel value={panelIndex} index={0} dir={theme.direction}>
            <div
              style={{
                marginBottom: "25px",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "left",
              }}
            >
              <StudentCard student={selectedStudent} attendance={attendance} />
              <ParentCard parent={parentInfo} />
            </div>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  gap: 2, // Adds spacing between the date pickers
                  flexWrap: "nowrap", // Prevents wrapping to the next line
                  width: "100%", // Ensures it stretches to fit the container
                }}
              >
                <DesktopDatePicker
                  label="Starting Date"
                  inputFormat="MM/DD/yyyy"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => (
                    <TextField {...params} sx={{ flex: 1, minWidth: "45%" }} />
                  )}
                />
                <DesktopDatePicker
                  label="Ending Date"
                  inputFormat="MM/DD/yyyy"
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={(params) => (
                    <TextField {...params} sx={{ flex: 1, minWidth: "45%" }} />
                  )}
                />
              </Box>
            </LocalizationProvider>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <NotesList notes={notes} />
            </div>
          </TabPanel>
          <TabPanel value={panelIndex} index={1} dir={theme.direction}>
            <div style={{ width: "100%" }}>
              <Box
                sx={{
                  width: "100%", // Ensures the panel spans the full width
                  display: "flex",
                  flexDirection: "column", // For vertical stacking
                  gap: 2, // Adds spacing between elements
                  padding: 2, // Adjust padding for consistency
                }}
              >
                <TextField
                  label="Notes"
                  variant="outlined"
                  multiline
                  rows={18}
                  style={{ width: "100%" }}
                  value={newNotes}
                  fullWidth // Makes the TextField span the full width of its parent
                  onChange={(e) => setNewNotes(e.target.value)}
                  sx={{ marginTop: 2 }}
                />
                <Button
                  sx={{
                    marginTop: 3,
                    backgroundColor: "#2C2C2C",
                    width: "50%", // Keeps button centered
                    alignSelf: "center", // Ensures it's horizontally centered
                  }}
                  disabled={!newNotes || loading.submitting}
                  onClick={handleSubmission}
                  color="primary"
                  variant="outlined"
                >
                  Submit
                </Button>
              </Box>
            </div>
          </TabPanel>
        </SwipeableViews>
      </Box>
    </Zoom>
  );

  const width = mobile ? "100%" : 720;

  return (
    <div
      style={{
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#121212",
        minWidth: mobile ? "100%" : "70vw",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <StudentSelector
          studentsData={students}
          studentAutocompleteValue={studentAutocompleteValue}
          handleStudentChange={handleStudentChange}
          studentsLoading={loading.students}
          mobile={mobile}
        />
        {loading.notes && studentSelected ? (
          <div
            style={{
              minHeight: "100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          renderTabsContent(width)
        )}
      </div>
    </div>
  );
};
